import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTabContent } from '../services/cms/cmsLayoutService';
import {
  fetchTabContentStart,
  fetchTabContentSuccess,
  fetchTabContentFailure,
  setCurrentTabSlug,
  getTabCacheKey,
  selectTabContentByCacheKey,
  selectTabContentLoadingByCacheKey,
  selectTabContentErrorByCacheKey,
} from '../store/slices/cmsSlice';

/**
 * Hook for lazy loading tab content
 *
 * @param {Object} options - Hook options
 * @param {number} options.productId - The product ID
 * @param {number} [options.modelId] - The model ID (for model-based products)
 * @param {string} options.tabSlug - The tab slug to fetch content for
 * @param {boolean} [options.autoFetch=true] - Whether to auto-fetch on mount/change
 * @returns {Object} Tab content state and actions
 */
export const useTabContent = ({
  productId,
  modelId,
  tabSlug,
  autoFetch = true,
}) => {
  const dispatch = useDispatch();

  // Generate cache key
  const cacheKey = useMemo(
    () => getTabCacheKey(productId, modelId, tabSlug),
    [productId, modelId, tabSlug]
  );

  // Selectors
  const cachedContent = useSelector((state) =>
    selectTabContentByCacheKey(state, cacheKey)
  );
  const isLoading = useSelector((state) =>
    selectTabContentLoadingByCacheKey(state, cacheKey)
  );
  const error = useSelector((state) =>
    selectTabContentErrorByCacheKey(state, cacheKey)
  );

  // Use ref to track if fetch is in progress to prevent duplicate calls
  const fetchInProgressRef = useRef(false);

  // Fetch function - stable reference, checks state via refs/selectors inside
  const fetchContent = useCallback(async () => {
    // Skip if no valid params
    if (!productId || !tabSlug) {
      return;
    }

    // Skip if fetch already in progress
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;
    dispatch(fetchTabContentStart({ cacheKey }));

    try {
      const content = await fetchTabContent({
        productId,
        modelId,
        tabSlug,
      });

      dispatch(fetchTabContentSuccess({ cacheKey, content }));
    } catch (err) {
      dispatch(
        fetchTabContentFailure({
          cacheKey,
          error: err.message || 'Failed to fetch tab content',
        })
      );
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [productId, modelId, tabSlug, cacheKey, dispatch]);

  // Auto-fetch on mount/change - only when cacheKey changes and content not cached
  useEffect(() => {
    // Skip if auto-fetch disabled or no tabSlug
    if (!autoFetch || !tabSlug || !productId) {
      return;
    }

    // Skip if already cached or loading
    if (cachedContent || isLoading) {
      return;
    }

    fetchContent();
    // Only depend on cacheKey to prevent re-fetching on every state change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Update current tab in Redux
  useEffect(() => {
    if (tabSlug) {
      dispatch(setCurrentTabSlug({ tabSlug }));
    }
  }, [tabSlug, dispatch]);

  return {
    content: cachedContent,
    isLoading,
    error,
    refetch: fetchContent,
    isCached: !!cachedContent,
    cacheKey,
  };
};

export default useTabContent;
